/**
 * Login.jsx — Email + password only.
 *
 * GOOGLE-REGISTRATION-ONLY (2026-02-27)
 *
 * Per the surgical-fix product decision, Google sign-in is reserved
 * EXCLUSIVELY for public registration identity verification. Workspace
 * access is controlled by email + password credentials issued by the
 * CertOpz administrator. This page therefore renders ONLY the
 * email/password form — no Google button, no Internal-users divider,
 * no Google-verification copy.
 *
 * Internal users (Owner, Admin, Employees) and the Platform Super
 * Admin continue to log in via this single form. Google login is
 * blocked server-side (403 GOOGLE_LOGIN_DISABLED) so legacy SPA builds
 * cached in user browsers cannot bypass this rule.
 */
import { useState } from "react";
import { Navigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Briefcase, FileCheck, BarChart3 } from "lucide-react";
import { usePlatformBranding, brandUrl } from "@/context/PlatformBrandingContext";

const DEFAULT_LOGO = "/logo.svg";

function safeRedirectTarget(raw) {
  if (!raw) return null;
  try {
    const decoded = decodeURIComponent(raw);
    if (decoded.startsWith("/") && !decoded.startsWith("//")) return decoded;
  } catch { /* ignore */ }
  return null;
}

function computePostLoginTarget(search, mode) {
  const sp = new URLSearchParams(search || "");
  const redirect = safeRedirectTarget(sp.get("redirect"));
  if (redirect) return redirect;
  const intent = sp.get("intent");
  if (intent === "paid" || intent === "trial" || intent === "enterprise") {
    return "/subscription/change-plan";
  }
  if (mode === "hr-portal") return "/my-hr";
  return "/";
}

export default function Login({ mode = "workspace" }) {
  const { user, login, error, rateLimit } = useAuth();
  const platformBranding = usePlatformBranding();
  // Login is a pre-auth platform surface — resolves ONLY to the platform
  // `login` asset (fallback: bundled /logo.svg). No tenant cascade.
  const LOGO = brandUrl(platformBranding?.login) || DEFAULT_LOGO;
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);
  // Phase TENANT-SUSPEND (P0 security 2026-07-09) — surface the
  // "organisation suspended" banner both on:
  //   (a) fresh visits from the `api.js` interceptor
  //       (`?reason=tenant_suspended` + `sessionStorage.tenantSuspendedNotice`)
  //   (b) failed login attempts (the `AuthContext.login` promise rejects
  //       with a 403 whose code is TENANT_SUSPENDED; `error` propagates
  //       here and its extracted code is checked at render time).
  const suspendedFromInterceptor = (() => {
    try {
      if (typeof window === "undefined") return false;
      if (new URLSearchParams(location.search).get("reason") === "tenant_suspended") return true;
      return sessionStorage.getItem("tenantSuspendedNotice") === "1";
    } catch { return false; }
  })();
  const suspendedFromLogin = (error?.code === "TENANT_SUSPENDED");
  const isTenantSuspended = suspendedFromInterceptor || suspendedFromLogin;

  // 2026-08-25 · Session-expiry / idle-timeout banner.
  // The `api.js` 401 interceptor OR the AuthContext idle timer both
  // stash a `sessionStorage.sessionExpiredNotice` marker and redirect
  // here with `?reason=session_expired` or `?reason=idle_timeout`.
  const sessionExpiry = (() => {
    try {
      if (typeof window === "undefined") return null;
      const reason = new URLSearchParams(location.search).get("reason");
      if (reason === "idle_timeout")   return "idle";
      if (reason === "session_expired") return "expired";
      const flag = sessionStorage.getItem("sessionExpiredNotice");
      if (flag === "idle") return "idle";
      if (flag)            return "expired";
      return null;
    } catch { return null; }
  })();
  // 2026-08-25 · Surface subscription validity outcomes as
  // account-safe explanatory banners. `SUBSCRIPTION_EXPIRED` /
  // `SUBSCRIPTION_NOT_YET_ACTIVE` bubble up from the backend
  // `assert_tenant_operational` guard.
  const subExpired    = (error?.code === "SUBSCRIPTION_EXPIRED");
  const subNotYet     = (error?.code === "SUBSCRIPTION_NOT_YET_ACTIVE");

  if (user) return <Navigate to={computePostLoginTarget(location.search, mode)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Clear the one-shot "your org was suspended" banner so a retry from
    // a different account or a reactivated tenant doesn't keep showing it.
    try { sessionStorage.removeItem("tenantSuspendedNotice"); } catch { /* ignore */ }
    // 2026-08-25 · Also clear the session-expired flag so a fresh
    // login attempt doesn't keep showing the banner across retries.
    try { sessionStorage.removeItem("sessionExpiredNotice"); } catch { /* ignore */ }
    try {
      await login(email.trim(), pwd);
    } finally {
      setLoading(false);
    }
  };

  // Login is a pre-authentication platform-owned surface — MUST use
  // platform branding only (never tenant branding), per the 2026-08-06
  // branding boundary. The pre-existing tenant-brand cascade is
  // reserved for authenticated tenant surfaces (workspace shell,
  // documents, portal). LOGO already resolves to the platform
  // `login` asset with the bundled CertOpz logo as final fallback.
  const displayName = "CertOpz";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50" data-testid="login-page">
      {/* Left panel — brand mark + headline */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="flex items-center gap-3">
          {/* BRAND-LOGO-FIX (2026-08-06): render inside a responsive
              rectangular bounding box using max-width/max-height +
              object-contain so the 640×200 login wordmark keeps its
              intrinsic proportions on desktop, tablet and mobile.
              Falls back to the packaged /logo.svg if the platform
              asset ever fails to load. */}
          <img
            src={LOGO}
            alt={displayName}
            className="object-contain"
            style={{ width: "auto", height: "auto",
                      maxWidth: "clamp(160px, 22vw, 280px)",
                      maxHeight: "clamp(40px, 6vw, 72px)" }}
            onError={(e) => { if (e.currentTarget.src !== DEFAULT_LOGO) e.currentTarget.src = DEFAULT_LOGO; }}
            data-testid="login-brand-logo"
          />
          <div className="leading-tight">
            <div className="text-xl font-bold tracking-tight" style={{ fontFamily: "Plus Jakarta Sans" }}>{displayName}</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-teal-300">Workspace</div>
          </div>
        </div>
        <div data-testid="login-enterprise-messaging">
          <h1 className="text-[2rem] xl:text-[2.25rem] font-extrabold leading-[1.1] tracking-tight text-white">
            Unified ERP for
          </h1>
          <p
            className="mt-2 font-bold leading-[1.15] tracking-tight text-[1.65rem] xl:text-[1.85rem]"
            style={{ color: "#2DD4BF" }}
            data-testid="login-product-statement"
          >
            Certification, Compliance,<br />
            HR &amp; Payroll, Finance,<br />
            and Renewals.
          </p>
          <div
            className="mt-6 h-[3px] w-16 rounded-full"
            style={{ backgroundColor: "#2DD4BF" }}
            aria-hidden="true"
          />
          <ul className="mt-6 space-y-3 text-sm text-white/90 font-medium" data-testid="login-feature-list">
            <li className="flex items-center gap-3">
              <Briefcase className="w-[18px] h-[18px] shrink-0" style={{ color: "#2DD4BF" }} />
              <span>Case-centric operations</span>
            </li>
            <li className="flex items-center gap-3">
              <FileCheck className="w-[18px] h-[18px] shrink-0" style={{ color: "#2DD4BF" }} />
              <span>Document control &amp; approvals</span>
            </li>
            <li className="flex items-center gap-3">
              <BarChart3 className="w-[18px] h-[18px] shrink-0" style={{ color: "#2DD4BF" }} />
              <span>Finance-aware workflows</span>
            </li>
            <li className="flex items-center gap-3">
              <ShieldCheck className="w-[18px] h-[18px] shrink-0" style={{ color: "#2DD4BF" }} />
              <span>Audit trail &amp; override control</span>
            </li>
          </ul>
        </div>
        <div>
          <div className="text-[11px] text-slate-400">© CertOpz ERP · UAE</div>
          {/* 2026-08-25 · Discrete Super Admin login shortcut. Sits
              below the copyright so it doesn't compete with the tenant
              login CTA but is one click away for the platform team. */}
          <Link
            to="/platform/login"
            data-testid="admin-login-link"
            className="mt-2 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-teal-300 transition-colors"
          >
            <ShieldCheck className="w-3 h-3" />
            <span>Admin login →</span>
          </Link>
          <div className="mt-2 text-[11px] text-slate-500">
            <Link to="/privacy" className="hover:text-teal-300 transition-colors">Privacy Policy</Link>
            <span className="text-slate-600"> · </span>
            <Link to="/terms" className="hover:text-teal-300 transition-colors">Terms of Use</Link>
          </div>
        </div>
      </div>

      {/* Right panel — login form */}
      <div className="flex items-center justify-center p-8 bg-white">
        <form onSubmit={submit} className="w-full max-w-md space-y-5" data-testid="login-form">
          <div className="space-y-2">
            <div className="lg:hidden flex items-center gap-3 mb-6">
              {/* BRAND-LOGO-FIX (2026-08-06): mobile mirror of the
                  desktop hero — platform-only branding on this pre-auth
                  surface, no tenant cascade, aspect preserved via
                  max-width/max-height + object-contain. */}
              <img
                src={LOGO}
                alt={displayName}
                className="object-contain"
                style={{ width: "auto", height: "auto",
                          maxWidth: 200, maxHeight: 44 }}
                onError={(e) => { if (e.currentTarget.src !== DEFAULT_LOGO) e.currentTarget.src = DEFAULT_LOGO; }}
                data-testid="login-brand-logo-mobile"
              />
            </div>
            <span className="text-xs uppercase font-semibold tracking-wider text-cyan-700">Sign in</span>
            <h2 className="text-3xl font-bold text-slate-900">Welcome back</h2>
            <p className="text-sm text-slate-500">
              Sign in with the credentials provided by your CertOpz administrator.
            </p>
          </div>

          {sessionExpiry && !isTenantSuspended ? (
            <div
              role="alert"
              data-testid="session-expired-banner"
              className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            >
              <div className="font-semibold">
                {sessionExpiry === "idle"
                  ? "You were signed out after 30 minutes of inactivity"
                  : "Your session has expired"}
              </div>
              <div className="mt-0.5 text-amber-900/80">
                For your security, please sign in again to continue.
              </div>
            </div>
          ) : null}

          {isTenantSuspended ? (
            <div
              role="alert"
              data-testid="tenant-suspended-banner"
              className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800"
            >
              <div className="font-semibold">This organisation is suspended</div>
              <div className="mt-0.5 text-rose-700/90">
                Please contact your administrator or CertOpz support to restore access.
              </div>
            </div>
          ) : null}

          {subExpired ? (
            <div
              role="alert"
              data-testid="subscription-expired-banner"
              className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800"
            >
              <div className="font-semibold">Subscription expired</div>
              <div className="mt-0.5 text-rose-700/90">
                Your CertOpz license has expired. Please contact CertOpz to renew.
              </div>
            </div>
          ) : null}

          {subNotYet ? (
            <div
              role="alert"
              data-testid="subscription-not-yet-active-banner"
              className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            >
              <div className="font-semibold">Subscription not yet active</div>
              <div className="mt-0.5 text-amber-800/90">
                Your CertOpz license is scheduled to begin later. Please contact CertOpz.
              </div>
            </div>
          ) : null}

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="login-email-input"
                value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="login-password-input"
                value={pwd} onChange={(e) => setPwd(e.target.value)} required autoComplete="current-password" />
            </div>
          </div>

          {rateLimit ? (
            <div className="text-sm text-amber-900 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2" data-testid="login-rate-limit">
              {rateLimit.retryAfter > 0
                ? `Too many login attempts. Please try again in ${Math.max(1, Math.ceil(rateLimit.retryAfter / 60))} minute${Math.ceil(rateLimit.retryAfter / 60) === 1 ? "" : "s"}.`
                : rateLimit.message || "Too many login attempts. Please try again later."}
            </div>
          ) : null}
          {error && !rateLimit && !isTenantSuspended ? (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">
              {error?.message || (typeof error === "string" ? error : "Login failed")}
            </div>
          ) : null}

          <Button type="submit" disabled={loading || !!rateLimit} data-testid="login-submit-button"
                  className="w-full h-11 text-base font-semibold" style={{ background: "#0891B2" }}>
            {loading ? "Signing in..." : rateLimit ? "Locked — try again later" : "Sign in to CertOpz"}
          </Button>

          <div className="text-xs text-slate-500 text-center pt-2">
            New to CertOpz?{" "}
            <Link to="/register" className="text-cyan-700 font-semibold hover:underline" data-testid="login-register-link">Create an account</Link>
            {" · "}
            <a href="/pricing" className="text-cyan-700 font-semibold hover:underline" data-testid="login-pricing-link">View plans →</a>
          </div>
          <div className="text-[11px] text-slate-400 text-center">
            <Link to="/privacy" className="hover:text-cyan-700 hover:underline" data-testid="login-privacy-link">Privacy Policy</Link>
            {" · "}
            <Link to="/terms" className="hover:text-cyan-700 hover:underline" data-testid="login-terms-link">Terms of Use</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
